import { useState, useEffect } from 'react';
import { createPortal } from "react-dom";
import Loader from '../../../components/globalloaders/Loader';
import { useLoading } from '../../../components/globalloaders/LoadingContext';
import { toast } from 'react-toastify';
import { Bars3Icon, EllipsisVerticalIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import { formatLabel, decryptPayload, formatCnic, formatDateTime, formatTableDateTime, encryptPayload } from '../../../constants/helpers';
import ConfirmDialog from '../../../components/dialogbox/ConfirmDialog';
import OpCodeDialog from "../../../components/dialogbox/OpCodeDialog";
import FallBack from '../../../components/dialogbox/FallBack';
import api from '../../../utils/auth/api';
import EmployeeAdd from '../components/employee/EmployeeAdd';
import EmployeeUpdate from '../components/employee/EmployeeUpdate';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { employeeMediaRoles } from '../../../constants/options';

const Employee = ({ setIsMobileOpen }) => {

  const mediaURI = `${process.env.REACT_APP_URI}/stream/internal-media`;

  const { isLoading, setIsLoading } = useLoading();

  const [loaders, setLoaders] = useState({
    fetch: false,
    details: false,
    search: false,
  });
  const [dialog, setDialog] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    confirmText: "",
    action: () => { },
  });
  const [opCodeDialog, setOpCodeDialog] = useState({
    open: false,
    id: null,
  });

  const [employeeData, setEmployeeData] = useState([]);

  // Menu List State
  const [menuState, setMenuState] = useState({
    open: false,
    rowId: null,
    rowData: null,
    position: { top: 0, left: 0 },
  });
  const openMenu = ({ id, position, row }) => {
    setMenuState({
      open: true,
      rowId: id,
      rowData: row,
      position,
    });
  };
  const closeMenu = () => {
    setMenuState({
      open: false,
      rowId: null,
      rowData: null,
      position: { top: 0, left: 0 },
    });
  };

  // For Search Quick & Deep
  const [quickResults, setQuickResults] = useState([]);
  const [deepResults, setDeepResults] = useState([]);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState("quick");
  const [isQuickSearching, setIsQuickSearching] = useState(false);
  const [totalBackendPg, setTotalBackendPg] = useState(1);
  const getDisplayData = () => {
    if (searchMode === "quick") {
      return isQuickSearching ? quickResults : employeeData;
    }
    if (searchMode === "deep") {
      return deepResults;
    }
    return employeeData;
  };

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const displayData = getDisplayData();
  const [totalPages, setTotalPages] = useState(1);
  const paginatedData = displayData;
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBackendPg);
  useEffect(() => {
    setQuery("");
    setQuickResults([]);
    setDeepResults([]);
    setIsQuickSearching(false);
    if (searchMode === "quick") {
      setCurrentPage(1);
      setTotalPages(Math.ceil(totalBackendPg / itemsPerPage));
    } else if (searchMode === "deep") {
      setCurrentPage(1);
      setTotalPages(1);
    }
  }, [searchMode, totalBackendPg, itemsPerPage]);

  // Details DialogBox with Api Call By ID 
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [formData1, setFormData1] = useState({
    employee: null,
    guarantors: [],
  });
  const hasViewData = !!formData1?.employee || formData1?.guarantors?.length > 0;
  const handleViewOpen = async (id) => {
    setIsViewOpen(true);
    setLoaders(prev => ({ ...prev, details: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/employee/detail/${id}`);
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setFormData1({
          employee: decryptedData.employee,
          guarantors: decryptedData.guarantors || [],
        });
      }
      else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching employee details:', error);
    } finally {
      setLoaders(prev => ({ ...prev, details: false }));
      return;
    }
  };
  const handleViewClose = () => {
    setIsViewOpen(false);
    setFormData1({
      employee: null,
      guarantors: [],
    });
  };
  const fetchMediaBlob = async (url) => {
    try {
      const response = await api.get(`${mediaURI}${url}`, {
        responseType: "blob", // important for images
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      console.error("Error fetching media:", err);
      return null;
    }
  };
  const rolePriority = employeeMediaRoles.reduce((acc, item, index) => {
    acc[item.value] = index;
    return acc;
  }, {});
  const MediaGallery = ({ media = [] }) => {
    // Loader State for Fullscreen-View & Initial Image 
    const [mediaLoading, setMediaLoading] = useState({});
    const [viewerLoading, setViewerLoading] = useState(true);
    const handleMediaLoad = (url) => {
      setMediaLoading((prev) => ({ ...prev, [url]: { loading: false, error: false } }));
    };
    const handleMediaError = (url) => {
      setMediaLoading((prev) => ({ ...prev, [url]: { loading: false, error: true } }));
    };
    const [viewerImage, setViewerImage] = useState(null);

    const [mediaUrls, setMediaUrls] = useState({});
    useEffect(() => {
      media.forEach(async (m) => {
        if (!mediaUrls[m.url]) {
          const blobUrl = await fetchMediaBlob(m.url);
          setMediaUrls((prev) => ({ ...prev, [m.url]: blobUrl }));
        }
      });
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [media]);

    if (!media.length) {
      return (
        <p className="text-sm text-gray-500 italic">
          No documents uploaded
        </p>
      );
    };
    const sortedMedia = [...media].sort((a, b) => {
      const aOrder = rolePriority[a.role] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = rolePriority[b.role] ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
    return (
      <>
        {/* Thumbnails */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sortedMedia.map((m, idx) => {
            const ext = m.url?.split(".").pop()?.toLowerCase();
            if (!["jpg", "jpeg", "png", "webp"].includes(ext)) return null;

            const mediaState = mediaLoading[m.url] || { loading: true, error: false };
            const blobUrl = mediaUrls[m.url];

            return (
              <div
                key={idx}
                className="relative w-full max-[370px]:w-full rounded-lg overflow-hidden bg-transparent"
                style={{
                  aspectRatio:
                    m.metadata?.width && m.metadata?.height
                      ? `${m.metadata.width} / ${m.metadata.height}`
                      : "16 / 9",
                  minHeight: 180,
                }}
              >
                {/* Loader Layer */}
                {mediaState.loading && !mediaState.error && (
                  <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-white/10 transition-opacity duration-500 z-10">
                    <Loader />
                  </div>
                )}

                {/* Error Overlay */}
                {mediaState.error && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black rounded-lg z-10">
                    <Square2StackIcon className="w-10 h-10 text-red-500 mb-2" />
                    <span className="text-red-400 font-semibold text-sm text-center px-2">
                      Image unavailable
                    </span>
                  </div>
                )}

                {/* Image */}
                <img
                  src={blobUrl}
                  alt={m.role}
                  draggable={false}
                  onLoad={() => handleMediaLoad(m.url)}
                  onError={() => handleMediaError(m.url)}
                  onClick={() => !mediaState.error && setViewerImage(blobUrl)}
                  className={`w-full h-full object-cover rounded-lg cursor-pointer transition-all duration-700
                  ${mediaState.loading || mediaState.error
                      ? "scale-105 blur-sm opacity-70"
                      : "scale-100 blur-0 opacity-100"
                    }`}
                />

                {/* Role Label */}
                <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-xs">
                  {formatLabel(m.role)}
                </div>
              </div>
            );
          })}
        </div>
        {/* Fullscreen Image Viewer */}
        {viewerImage && createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => {
              setViewerImage(null);
              setViewerLoading(true);
            }}
          >
            <div className="relative max-h-[90vh] max-w-[90vw] flex items-center justify-center">
              {/* Loader Overlay */}
              {viewerLoading && (
                <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-white/10 rounded-lg transition-opacity duration-300">
                  <Loader />
                </div>
              )}
              {/* Image */}
              <img
                src={viewerImage}
                alt="Full view"
                onClick={(e) => e.stopPropagation()} // prevent close
                onLoad={() => setViewerLoading(false)}
                onContextMenu={(e) => e.preventDefault()}
                className={`max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl transition-all duration-700 ${viewerLoading
                  ? "scale-105 blur-sm opacity-70"
                  : "scale-100 blur-0 opacity-100"
                  }`}
              />
            </div>
            {/* Close Button */}
            <button
              className="absolute top-5 right-5 bg-white/20 hover:bg-white/30 rounded-full p-2"
              onClick={() => {
                setViewerImage(null);
                setViewerLoading(true);
              }}
            >
              <XMarkIcon className="w-6 h-6 text-white" />
            </button>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Update DialogBox with Api Call and Send
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const handleEmployeeUpdated = (updatedEmployee) => {
    setEmployeeData(prev =>
      prev.map(emp =>
        emp._id === updatedEmployee._id
          ? { ...emp, ...updatedEmployee }
          : emp
      )
    );
  };

  // QUICK SEARCH (local state)
  const handleQuickSearch = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (searchMode === "quick") {
      if (value.trim() === "") {
        setIsQuickSearching(false);
        setQuickResults(employeeData);
      } else {
        setIsQuickSearching(true);
        const results = employeeData.filter((s) =>
          s.name.toLowerCase().includes(value.toLowerCase()) ||
          s.father_husband_name.toLowerCase().includes(value.toLowerCase()) ||
          s.phone?.toLowerCase().includes(value.toLowerCase()) ||
          s.employeeId?.toLowerCase().includes(value.toLowerCase())
        );
        setQuickResults(results);
      }
    }
  };
  // DEEP SEARCH (API)
  const handleDeepSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      toast.warning("Enter value to search");
      return;
    }
    setLoaders(prev => ({ ...prev, search: true }));
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/employee/search`, {
        params: { value: searchQuery },
      });
      if (response.data.success) {
        const decryptedData = decryptPayload(response.data.encryptedData);
        setDeepResults(decryptedData);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error deep searching employee:", error);
      toast.error(error?.response.data.message || "Deep search employee failed!");
    } finally {
      setLoaders(prev => ({ ...prev, search: false }));
      return;
    }
  };
  // Connect Search to Quick or Deep via searchMode
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchMode === "quick") {
      handleQuickSearch({ target: { value: query } });
    } else if (searchMode === "deep") {
      handleDeepSearch(query);
    }
  };

  // Fetch All API
  useEffect(() => {
    const fetchData = async (page = 1) => {
      setLoaders(prev => ({ ...prev, fetch: true }));
      try {
        const response = await api.get(`${process.env.REACT_APP_URI}/employee/get-all`, {
          params: { page, limit: itemsPerPage },
        });
        if (response.data.success) {
          const decryptedData = decryptPayload(response.data.encryptedData);
          setEmployeeData(decryptedData.data);
          setQuickResults(decryptedData.data);
          setTotalBackendPg(decryptedData.total);
          setTotalPages(Math.ceil(decryptedData.total / itemsPerPage));
        }
        else {
          toast.error(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoaders(prev => ({ ...prev, fetch: false }));
        return;
      }
    };
    fetchData(currentPage);
  }, [currentPage]);

  // Delete API By ID
  const handleDelete = async (id, opcode) => {
    if (isLoading) return;
    setIsLoading(true);
    const payload = encryptPayload({ id, opcode });
    try {
      const response = await api.delete(`${process.env.REACT_APP_URI}/employee/delete`, { data: { data: payload } });
      if (response.data.success) {
        setEmployeeData((prevData) => prevData.filter((data) => data._id !== id));
        toast.success(response.data.message);
      }
    }
    catch (error) {
      console.log("Error occur while deleting employee:", error);
      if (error.response && error.response.data) {
        const data = error.response.data;
        toast.error(data.message);
      } else {
        toast.error("Unable to Delete Employee Network Error, please try again!");
      }
    }
    finally {
      setIsLoading(false);
      return;
    }
  };

  // === Add DialogBox Code ===
  const [isAddDialog, setAddDialog] = useState(false);
  const handleEmployeeCreated = (newEmployee) => {
    setEmployeeData(prev => [newEmployee, ...prev]);
  };

  // PDF Generation helpers & handlers
  const getMediaByRole = (media = [], role) => {
    return media.find(m => m.role === role) || null;
  };
  const readMediaAsArrayBuffer = async (url) => {
    if (!url) return null;
    try {
      const res = await api.get(`${mediaURI}${url}`, {
        responseType: "arraybuffer"
      });
      return res.data;
    } catch (err) {
      console.error("Error fetching media for PDF:", err);
      return null;
    }
  };
  const getMimeFromUrl = (url = "") => {
    const u = url.toLowerCase();
    if (u.endsWith(".png")) return "image/png";
    if (u.endsWith(".jpg") || u.endsWith(".jpeg")) return "image/jpeg";
    return "unknown";
  };
  const handleIndividualPDF = async (id) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.get(`${process.env.REACT_APP_URI}/employee/detail/${id}`);
      if (!response.data.success) {
        toast.error(response.data.message);
        return;
      }

      const decrypted = decryptPayload(response.data.encryptedData);
      const { employee, guarantors = [] } = decrypted;
      const guarantor = guarantors[0] || null; // same behavior as old form

      /* ---------- PDF INIT ---------- */
      const submissionDate = new Date().toISOString();
      const customFormattedDate = formatDateTime(submissionDate);

      const pdfTemplateUrl = "/mbeo-guaranteeform.pdf";
      const existingPdfBytes = await fetch(pdfTemplateUrl).then(res =>
        res.arrayBuffer()
      );
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      const eStampBytes = await fetch("/vstamp.png").then(res =>
        res.arrayBuffer()
      );
      const eStampImage = await pdfDoc.embedPng(eStampBytes);
      const eStampDims = eStampImage.scale(0.10);

      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const secondPage = pages[1];

      let thirdPage, forthPage, fifthPage;

      /* ---------- MEDIA RESOLUTION ---------- */
      const empMedia = employee.media || [];
      const gMedia = guarantor?.media || [];

      const mediaMap = {
        employeeProfile: getMediaByRole(empMedia, "profile"),
        employeeCnicFront: getMediaByRole(empMedia, "cnic_front"),
        employeeCnicBack: getMediaByRole(empMedia, "cnic_back"),
        employeeFingerprint: getMediaByRole(empMedia, "fingerprint"),
        employeeSignature: getMediaByRole(empMedia, "signature"),
        employeeLicenseFront: getMediaByRole(empMedia, "driving_license_front"),
        employeeLicenseBack: getMediaByRole(empMedia, "driving_license_back"),
        employeeBform: getMediaByRole(empMedia, "bform"),

        guarantorCnicFront: getMediaByRole(gMedia, "cnic_front"),
        guarantorCnicBack: getMediaByRole(gMedia, "cnic_back"),
        guarantorFingerprint: getMediaByRole(gMedia, "fingerprint"),
        guarantorSignature: getMediaByRole(gMedia, "signature"),
      };

      const buffers = await Promise.all(
        Object.values(mediaMap).map(m =>
          m ? readMediaAsArrayBuffer(m.url) : Promise.resolve(null)
        )
      );

      const embedSafe = async (buffer, mime) => {
        if (!buffer) return null;
        if (mime === "image/png") return pdfDoc.embedPng(buffer);
        if (mime === "image/jpeg") return pdfDoc.embedJpg(buffer);
        return null;
      };

      const [
        employeeProfileImage,
        employeeCnicFrontImage,
        employeeCnicBackImage,
        employeeFingerprintImage,
        employeeSignatureImage,
        employeeLicenseFrontImage,
        employeeLicenseBackImage,
        employeeBformImage,

        guarantorCnicFrontImage,
        guarantorCnicBackImage,
        guarantorFingerprintImage,
        guarantorSignatureImage,
      ] = await Promise.all(
        Object.values(mediaMap).map((m, i) =>
          embedSafe(buffers[i], getMimeFromUrl(m?.url))
        )
      );

      /* ---------- IMAGE PAGES (UNCHANGED COORDS) ---------- */
      if (employeeProfileImage || employeeCnicFrontImage || employeeCnicBackImage || employeeFingerprintImage || employeeSignatureImage || employeeLicenseFrontImage || employeeLicenseBackImage) {
        thirdPage = pdfDoc.addPage();
        thirdPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Employee ID: ${employee.employeeId}`, { x: 120, y: 830, size: 10 });

        employeeProfileImage &&
          thirdPage.drawImage(employeeProfileImage, { x: 10, y: 410, width: 240, height: 410 });

        employeeCnicFrontImage &&
          thirdPage.drawImage(employeeCnicFrontImage, { x: 265, y: 620, width: 320, height: 200 });
        employeeCnicBackImage &&
          thirdPage.drawImage(employeeCnicBackImage, { x: 265, y: 410, width: 320, height: 200 });

        employeeFingerprintImage &&
          thirdPage.drawImage(employeeFingerprintImage, { x: 10, y: 210, width: 285, height: 190 });
        employeeSignatureImage &&
          thirdPage.drawImage(employeeSignatureImage, { x: 300, y: 210, width: 285, height: 190 });

        employeeLicenseFrontImage &&
          thirdPage.drawImage(employeeLicenseFrontImage, { x: 10, y: 10, width: 285, height: 190 });
        employeeLicenseBackImage &&
          thirdPage.drawImage(employeeLicenseBackImage, { x: 300, y: 10, width: 285, height: 190 });

      }

      if (employeeBformImage) {
        forthPage = pdfDoc.addPage();
        forthPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Employee ID: ${employee.employeeId}`, { x: 120, y: 820, size: 10 });
        forthPage.drawImage(employeeBformImage, { x: 23, y: 30, width: 550, height: 770 });
      }

      if (guarantorCnicFrontImage || guarantorCnicBackImage || guarantorFingerprintImage || guarantorSignatureImage) {
        fifthPage = pdfDoc.addPage();
        fifthPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Employee ID: ${employee.employeeId}`, { x: 120, y: 820, size: 10 });
        guarantorCnicFrontImage &&
          fifthPage.drawImage(guarantorCnicFrontImage, { x: 10, y: 610, width: 285, height: 190 });
        guarantorCnicBackImage &&
          fifthPage.drawImage(guarantorCnicBackImage, { x: 300, y: 610, width: 285, height: 190 });
        guarantorFingerprintImage &&
          fifthPage.drawImage(guarantorFingerprintImage, { x: 10, y: 410, width: 285, height: 190 });
        guarantorSignatureImage &&
          fifthPage.drawImage(guarantorSignatureImage, { x: 300, y: 410, width: 285, height: 190 });
      }

      /* ---------- TEXT DRAWING (FIELD MAPPED ONLY) ---------- */
      firstPage.drawText(`${formatLabel(employee.category)}`, { x: 100, y: 675, size: 12 });
      firstPage.drawText(`${formatDateTime(employee.date)}`, { x: 460, y: 675, size: 12 });
      firstPage.drawText(`${formatLabel(employee.duty_time)}`, { x: 50, y: 648, size: 12 });
      firstPage.drawText(`${employee.name}`, { x: 482, y: 618, size: 12 });
      firstPage.drawText(`${employee.father_husband_name}`, { x: 370, y: 618, size: 12 });
      firstPage.drawText(`${employee.grandfather_name || ""}`, { x: 260, y: 618, size: 12 });
      firstPage.drawText(`${formatLabel(employee.married_status)}`, { x: 50, y: 618, size: 12 });
      firstPage.drawText(`${employee.kid_amount || ""}`, { x: 490, y: 585, size: 12 });
      firstPage.drawText(`${formatLabel(employee.kid_gender) || ""}`, { x: 340, y: 585, size: 12 });
      firstPage.drawText(`${employee.kid_age || ""}`, { x: 220, y: 585, size: 12 });
      firstPage.drawText(`${formatLabel(employee.parent_status)}`, { x: 30, y: 585, size: 12 });
      firstPage.drawText(`${employee.age} year`, { x: 475, y: 552, size: 12 });
      firstPage.drawText(`${employee.caste}`, { x: 320, y: 552, size: 12 });
      firstPage.drawText(`${formatLabel(employee.religion)}`, { x: 180, y: 552, size: 12 });
      firstPage.drawText(`${formatLabel(employee.education)}`, { x: 25, y: 552, size: 12 });
      firstPage.drawText(`${employee.experience} year`, { x: 465, y: 518, size: 12 });
      firstPage.drawText(`${employee.known_place}`, { x: 25, y: 518, size: 12 });
      firstPage.drawText(`${employee.phone}`, { x: 25, y: 485, size: 12 });
      firstPage.drawText(`${employee.otherPhone || "-"}`, { x: 120, y: 485, size: 12 });
      firstPage.drawText(`${employee.address}`, { x: 25, y: 449, size: 12 });
      firstPage.drawText(`${formatCnic(employee.cnic_no)}`, { x: 325, y: 412, size: 19 });

      if (guarantor) {
        firstPage.drawText(`${guarantor.name}`, { x: 482, y: 333, size: 12 });
        firstPage.drawText(`${guarantor.father_husband_name}`, { x: 365, y: 333, size: 12 });
        firstPage.drawText(`${formatLabel(guarantor.relation)}`, { x: 240, y: 333, size: 12 });
        firstPage.drawText(`${guarantor.age || ""} year`, { x: 155, y: 333, size: 12 });
        firstPage.drawText(`${formatLabel(guarantor.religion)}`, { x: 25, y: 333, size: 12 });
        firstPage.drawText(`${guarantor.caste || ""}`, { x: 490, y: 298, size: 12 });
        firstPage.drawText(`${guarantor.occupation || ""}`, { x: 312, y: 298, size: 12 });
        firstPage.drawText(`${guarantor.phone}`, { x: 25, y: 298, size: 12 });
        firstPage.drawText(`${guarantor.address || ""}`, { x: 25, y: 262, size: 12 });
        firstPage.drawText(`${formatCnic(guarantor.cnic_no)}`, { x: 325, y: 220, size: 19 });
      }

      firstPage.drawText(`Employee ID: ${employee.employeeId}`, { x: 35, y: 90, size: 12 });
      firstPage.drawImage(eStampImage, {
        x: 30,
        y: 5,
        width: eStampDims.width,
        height: eStampDims.height,
      });
      firstPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Employee ID: ${employee.employeeId}`, { x: 120, y: 780, size: 10 });

      secondPage.drawText(`${employee.name}`, { x: 380, y: 238, size: 12 });
      secondPage.drawText(`${employee.father_husband_name}`, { x: 105, y: 238, size: 12 });

      secondPage.drawText(`Generated by MBEOWS | ${customFormattedDate} | Employee ID: ${employee.employeeId}`, { x: 120, y: 780, size: 10 });
      secondPage.drawImage(eStampImage, {
        x: 270,
        y: 5,
        width: eStampDims.width,
        height: eStampDims.height,
      });

      const pdfBytes = await pdfDoc.save();
      saveAs(new Blob([pdfBytes], { type: "application/pdf" }),
        `GuaranteeForm (Employee-${employee.employeeId}).pdf`
      );

      toast.success("PDF Generated");
    } catch (err) {
      console.error('Error fetching employee details for pdf:', err);
      toast.error(err?.response.data.message || "Failed to generate employee PDF!");
    } finally {
      setIsLoading(false);
      return;
    }
  };

  // Close menu list on outside click
  useEffect(() => {
    if (!menuState.open) return;
    const handleOutside = (e) => {
      const isTrigger = e.target.closest('[data-menu-trigger="true"]');
      const isMenu = e.target.closest('[data-floating-menu="true"]');
      if (isTrigger || isMenu) return;
      closeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", closeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", closeMenu);
    };
  }, [menuState.open]);

  // actions btn list
  const menuActionList = [
    {
      label: "PDF", type: "pdf", color: "text-green-600", hover: "hover:bg-green-100",
      boxType: "info", message: "Do you want to generate a PDF for this employee, including guarantor details if available?",
    },
    {
      label: "Edit", type: "edit", color: "text-blue-600", hover: "hover:bg-blue-100",
      boxType: "info", message: "Do you want to edit this employee details?",
    },
    {
      label: "Delete", type: "delete", color: "text-red-600", hover: "hover:bg-red-100",
      boxType: "danger",
      message: "This will permanently delete the employee, all agreements with this client, and all related entries for each agreement. This action cannot be undone.",
    },
  ];

  return (
    <>
      <ConfirmDialog
        isOpen={dialog.open}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.action}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        type={dialog.type}
      />
      <OpCodeDialog
        isOpen={opCodeDialog.open}
        type="danger"
        title="Process Authorization"
        message="By proceeding, you accept responsibility for any resulting data loss or modification. Please enter the OpCode to continue."
        confirmText="Verify"
        onClose={() =>
          setOpCodeDialog({ open: false, id: null })
        }
        onConfirm={(code) => {
          handleDelete(opCodeDialog.id, code);
        }}
      />
      {/* Three Dot Menu UI */}
      {menuState.open && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <div
            data-floating-menu="true"
            className="absolute bg-white border shadow-xl rounded-xl p-2 w-44 space-y-1 pointer-events-auto"
            style={{
              top: menuState.position.top,
              left: menuState.position.left,
            }}
          >
            {menuActionList.map((action) => (
              <button
                key={action.type}
                className={`flex items-center w-full gap-2 p-2 rounded-lg ${action.hover} text-sm font-medium cursor-pointer`}
                onClick={() => {
                  closeMenu();
                  if (action.type === "pdf") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "PDF Generation",
                      message: action.message,
                      confirmText: "Generate",
                      action: () => handleIndividualPDF(menuState.rowId),
                    });
                    return;
                  }
                  if (action.type === "edit") {
                    setDialog({
                      open: true,
                      type: action.boxType,
                      title: "Edit Employee",
                      message: action.message,
                      confirmText: action.label,
                      action: () => {
                        setUpdateId(menuState.rowId);
                        setIsUpdateOpen(true);
                      },
                    });
                    return;
                  }
                  setDialog({
                    open: true,
                    type: action.boxType,
                    title: "Delete Employee",
                    message: action.message,
                    confirmText: "Proceed",
                    action: () => {
                      setOpCodeDialog({
                        open: true,
                        id: menuState.rowId,
                      });
                    },
                  });
                }}
              >
                <span className={action.color}>{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Details & Update UI */}
      {isViewOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px] z-40 p-3">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto animate-fadeIn border border-gray-200">
            <div className="max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 sm:p-4 shadow-md text-center">
                <h2 className="text-xl sm:text-2xl font-bold">Employee Details</h2>
                <p className="text-[10px] sm:text-xs opacity-80">Smart by Logic. Built for You.</p>
              </div>
              {loaders.details ? (
                <div className="flex justify-center items-center h-[45vh]">
                  <Loader />
                </div>
              ) : !hasViewData ? (
                <FallBack
                  title="Employee details not available"
                  message="This Employee record could not be loaded. It may have been removed, or there was a network issue."
                  actionText="Close"
                  onAction={handleViewClose}
                />
              ) : (
                <div className="flex-1 space-y-4 overflow-auto px-2.5 sm:px-4 py-5 bg-gray-50">
                  {formData1.employee && (
                    <div className="space-y-5">
                      {/* Name Row */}
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-semibold">
                          {formData1.employee.name?.charAt(0)}
                        </div>

                        <div className="min-w-0">
                          <p className="text-lg font-semibold text-gray-900 truncate">
                            {formData1.employee.name}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            ID · {formData1.employee.employeeId}
                          </p>
                        </div>
                      </div>

                      {/* Info grid */}
                      <div className="pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 text-[15px]">
                        {[
                          ["Category", formatLabel(formData1.employee.category)],
                          ["Date", formatDateTime(formData1.employee.date)],
                          ["Duty Time", formatLabel(formData1.employee.duty_time)],
                          ["Gender", formatLabel(formData1.employee.gender)],
                          ["Age", `${formData1.employee.age} year`],
                          ["Phone", formData1.employee.phone],
                          ["Other Phone", formData1.employee.otherPhone],
                          ["CNIC", formatCnic(formData1.employee.cnic_no)],
                          ["Education", formatLabel(formData1.employee.education)],
                          ["Experience", `${formData1.employee.experience} year`],
                          ["Father / Husband", formData1.employee.father_husband_name],
                          ["Grandfather", formData1.employee.grandfather_name],
                          ["Parent Status", formatLabel(formData1.employee.parent_status)],
                          ["Caste", formData1.employee.caste],
                          ["Religion", formatLabel(formData1.employee.religion)],
                          ["Marital Status", formatLabel(formData1.employee.married_status)],
                          ["Children", formData1.employee.kid_amount],
                          ["Children Gender", formatLabel(formData1.employee.kid_gender)],
                          ["Children Age", formData1.employee.kid_age],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <p className="text-gray-400 text-sm">{label}</p>
                            <p className="text-gray-900 font-medium">{value || "—"}</p>
                          </div>
                        ))}
                      </div>

                      {/* Known Place & Address */}
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8'>
                        <div>
                          <p className="text-sm text-gray-400 mb-1">Known Place</p>
                          <p className="text-[15px] text-gray-900 leading-relaxed">
                            {formData1.employee.known_place}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-400 mb-1">Address</p>
                          <p className="text-[15px] text-gray-900 leading-relaxed">
                            {formData1.employee.address}
                          </p>
                        </div>
                      </div>

                      {/* Media */}
                      <div className="pt-4 border-t py-3">
                        <p className="text-base font-medium text-gray-800 mb-3">
                          Documents
                        </p>
                        <MediaGallery media={formData1.employee.media} />
                      </div>
                    </div>
                  )}
                  {formData1.guarantors.length > 0 && (
                    <div className="pt-4 border-t space-y-4">
                      <p className="text-base font-semibold text-gray-900">
                        Guarantors
                      </p>
                      {formData1.guarantors.map((g, i) => (
                        <div
                          key={g._id}
                          className="rounded-xl border border-gray-200 bg-white p-4 space-y-4"
                        >
                          {/* Title */}
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Guarantor #{i + 1}
                            </p>
                          </div>

                          {/* Info */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 text-[15px]">
                            {[
                              ["Name", g.name],
                              ["Father / Husband", g.father_husband_name],
                              ["Gender", formatLabel(g.gender)],
                              ["Age", `${g.age} year`],
                              ["CNIC", formatCnic(g.cnic_no)],
                              ["Phone", g.phone],
                              ["Relation", formatLabel(g.relation)],
                              ["Religion", formatLabel(g.religion)],
                              ["Caste", g.caste],
                              ["Occupation", g.occupation],
                            ].map(([label, value]) => (
                              <div key={label}>
                                <p className="text-gray-400 text-sm">{label}</p>
                                <p className="text-gray-900 font-medium">{value || "—"}</p>
                              </div>
                            ))}
                          </div>

                          {/* Address */}
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Address</p>
                            <p className="text-[15px] text-gray-900 leading-relaxed">
                              {g.address || "—"}
                            </p>
                          </div>

                          {/* Media */}
                          <MediaGallery media={g.media} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Btn */}
              {hasViewData && (
                <div className="border-t p-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between">
                  <span className='text-xs'>
                    <span className="font-medium text-gray-600">Updated At:</span>{" "}
                    {formatTableDateTime(formData1.employee.updatedAt)}
                  </span>
                  <button
                    type='button'
                    onClick={() => handleViewClose()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base bg-blue-600 hover:bg-blue-500 text-white font-semibold transition"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <EmployeeUpdate
        open={isUpdateOpen}
        Id={updateId}
        onClose={() => {
          setIsUpdateOpen(false);
          setUpdateId(null);
        }}
        onSuccess={handleEmployeeUpdated}
      />
      <div className="w-full">
        {/* Heading */}
        <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              className="md:hidden p-2 bg-white shadow rounded-lg"
              onClick={() => setIsMobileOpen(true)}
            >
              <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
            </button>
            <div className="flex flex-wrap items-center">
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-blue-600">
                Empl
              </h1>
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wide text-gray-800">
                oyee
              </span>
            </div>
          </div>

          <div>
            <button
              className="px-4 sm:px-6 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 font-medium text-sm sm:text-base"
              onClick={() => setAddDialog(true)}
            >
              Add
            </button>
            <EmployeeAdd
              open={isAddDialog}
              onClose={() => setAddDialog(false)}
              onSuccess={handleEmployeeCreated}
            />
          </div>
        </div>

        <hr className="border-t border-gray-200 my-4" />

        {/* Data Display Code */}
        <div className='w-full'>
          {/* Quick & Deep Search Bar */}
          <div className="w-full flex justify-center mb-7">
            <form
              onSubmit={handleSearchSubmit}
              className="relative w-full max-w-2xl flex items-center"
            >
              {/* Search Input */}
              <div className="relative flex-1">
                <input
                  type="search"
                  placeholder={`${searchMode === "quick" ? "Quick" : "Deep"} search . . .`}
                  value={query}
                  onChange={handleQuickSearch}
                  className="w-full py-3 pl-10 sm:pl-12 pr-32 sm:pr-40 rounded-2xl bg-white/80 backdrop-blur-sm 
                             border border-gray-200 shadow-md focus:ring-2 focus:ring-indigo-400 
                             focus:outline-none text-gray-700 placeholder-gray-400 transition"
                />
                <MagnifyingGlassIcon className="absolute w-5 h-5 left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              {/* Mode Toggle */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex rounded-full overflow-hidden border border-gray-200 shadow">
                <button
                  type="button"
                  onClick={() => setSearchMode("quick")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "quick"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Quick
                </button>
                <button
                  type="button"
                  onClick={() => setSearchMode("deep")}
                  className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all duration-150 ${searchMode === "deep"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  Deep
                </button>
              </div>
            </form>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto h-[70vh]">
            {(loaders.fetch || loaders.search) ? (
              <div className="flex justify-center items-center h-full p-6">
                <Loader />
              </div>
            ) : displayData.length === 0 ? (
              <div className="flex justify-center items-center h-full p-6">
                <p className="font-semibold text-gray-600 text-xl sm:text-md">
                  {searchMode === "quick" && query.trim()
                    ? "No Match !!"
                    : searchMode === "deep"
                      ? "No Records !!"
                      : "No Employee !!"}
                </p>
              </div>
            ) : (
              <>
                <table className="w-full bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "#",
                        "ID",
                        "Name",
                        "Father/Husband",
                        "Phone",
                        "Created",
                        "Action",
                      ].map((header, index) => (
                        <th
                          key={index}
                          className="p-3 text-sm font-semibold text-gray-600 text-center border-b-2 border-gray-200"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((data, index) => (
                      <tr key={data._id || index} className="hover:bg-gray-50 even:bg-gray-100">
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.employeeId}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatLabel(data.name)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatLabel(data.father_husband_name)}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {data.phone}
                        </td>
                        <td className="p-3 text-sm text-gray-700 text-center border-b border-gray-200">
                          {formatTableDateTime(data.createdAt)}
                        </td>
                        <td className="p-3 text-center border-b border-gray-200">
                          <div className="flex justify-center space-x-2">
                            <button
                              type='button'
                              className="px-2 sm:px-4 py-2 rounded-xl bg-blue-500 text-white hover:bg-blue-600 font-medium text-sm sm:text-base"
                              onClick={() => {
                                setDialog({
                                  open: true,
                                  type: "info",
                                  title: "View Details",
                                  message: "Want to view details for this employee?",
                                  confirmText: "View",
                                  action: () => handleViewOpen(data._id),
                                })
                              }}
                            >
                              Details
                            </button>

                            <button
                              type="button"
                              data-menu-trigger="true"
                              className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (menuState.open && menuState.rowId === data._id) {
                                  closeMenu();
                                  return;
                                }
                                const rect = e.currentTarget.getBoundingClientRect();
                                openMenu({
                                  id: data._id,
                                  row: data,
                                  position: {
                                    top: rect.bottom + window.scrollY - 180,
                                    left: rect.right + window.scrollX - 180,
                                  },
                                });
                              }}
                            >
                              <EllipsisVerticalIcon className="h-6 w-6 text-gray-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

          </div>

          {/* Pagination */}
          {searchMode === "quick" && totalPages > 1 && (
            <div className="w-full flex flex-col sm:flex-row justify-between items-center p-4 gap-2 sm:gap-4">
              {/* Showing Entries Text */}
              <span className="text-sm text-gray-600 text-center sm:text-left">
                Showing {startIndex} to {endIndex} of {totalBackendPg} entries
              </span>
              {/* Pagination Buttons */}
              <div className="flex flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={loaders.fetch || currentPage === 1}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                {/* Page Numbers */}
                {(() => {
                  const buttons = [];
                  const pageRange = 1; // current ±2 pages
                  let startPage = Math.max(currentPage - pageRange, 1);
                  let endPage = Math.min(currentPage + pageRange, totalPages);

                  if (startPage > 1) {
                    buttons.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) buttons.push(<span key="start-ellipsis" className="px-2 hidden sm:inline">...</span>);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    buttons.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis" className="px-2 hidden sm:inline">...</span>);
                    buttons.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return buttons;
                })()}
                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={loaders.fetch || currentPage === totalPages}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Employee;